import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_PASSWORD } from './fieldLimits';
import { autofillSx } from './fieldStyles';

interface Labels {
  newPassword: string;
  confirm: string;
  /** Shown under the confirmation field while the two differ. */
  mismatch: string;
}

/**
 * The new-password + confirm pair shared by NewPasswordForm (first sign-in) and
 * ResetPasswordForm (forgot password).
 *
 * Both screens need the same three behaviours — one eye toggle driving both fields,
 * the same mismatch rule, and the autofill override — and they were duplicated across
 * the two forms, which is how they drift apart. The caller supplies its own labels and
 * keeps its own submit logic; everything visual and stateful about the pair lives here.
 *
 * The toggle is rendered on the first field only. It was previously on both, and because
 * they share one `showPassword` either icon moved both fields, which QC read as a bug
 * (CNA-1995). One icon for one piece of state is what the behaviour actually is.
 *
 * `autoFocus` is opt-in because only one field on a form may claim focus: the first
 * password takes it on NewPasswordForm, but ResetPasswordForm gives it to the code field.
 */
export const usePasswordPair = (labels: Labels, autoFocus = false) => {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const mismatch = confirm.length > 0 && newPassword !== confirm;
  const type = showPassword ? 'text' : 'password';

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShowPassword((v) => !v)}
        edge="end"
        aria-label={t('login.local.togglePassword')}
      >
        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
      </IconButton>
    </InputAdornment>
  );

  const fields = (
    <>
      <TextField
        label={labels.newPassword}
        type={type}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        required
        autoFocus={autoFocus}
        fullWidth
        size="small"
        autoComplete="new-password"
        inputProps={{ maxLength: MAX_PASSWORD }}
        InputProps={{ endAdornment }}
        sx={autofillSx}
      />

      <TextField
        label={labels.confirm}
        type={type}
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        required
        fullWidth
        size="small"
        autoComplete="new-password"
        error={mismatch}
        helperText={mismatch ? labels.mismatch : undefined}
        inputProps={{ maxLength: MAX_PASSWORD }}
        sx={autofillSx}
      />
    </>
  );

  return {
    newPassword,
    confirm,
    mismatch,
    fields,
    /** True while the pair cannot be submitted — empty, or not matching. */
    isIncomplete: !newPassword || !confirm || mismatch,
  };
};
